// Typed wrappers for `/api/staff/assets/*` (F-C admin asset surface).
//
// Visitor `/api/assets/{id}` is consumed directly as an `<img src>` —
// no client wrapper needed. The staff endpoints below cover the
// minimal asset-detail page + the admin purge flow.

import { getApiClient } from "./client";

// Backend base URL from the build-time env (empty string = same-origin). CRA
// reads process.env directly; Vite shims it via `define` (see the vite configs).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const API_PREFIX = "/api";

/**
 * Absolute URL for an asset's bytes (e.g. a cover image): `<base>/api/assets/<id>`.
 * Returns "" for a nullish id so callers never render `src=".../assets/undefined"`.
 * Single source of truth — was hand-built in ~10 staff sites with 4 spellings.
 */
export function assetUrl(id: string | null | undefined): string {
  return id ? `${BACKEND_URL}${API_PREFIX}/assets/${id}` : "";
}

/**
 * Small list-row variant (≈160px JPEG) of an image asset — `?variant=thumb`.
 * The backend generates it on first request and persists it, falling back to
 * the original for non-images, so this is safe on any cover id. Use it for
 * thumbnails in lists; keep `assetUrl` for form previews and the visitor app.
 */
export function assetThumbUrl(id: string | null | undefined): string {
  const base = assetUrl(id);
  return base ? `${base}?variant=thumb` : "";
}

export type AssetState = "active" | "replaced" | "archived" | "purged";
export type StorageTier = "hot" | "cold";

export interface Asset {
  id: string;
  kind: string;
  mime_type: string;
  storage_uri: string;
  storage_tier: StorageTier;
  size_bytes: number;
  checksum: string;
  uploaded_by: string;
  uploaded_at: string;
  owner_kind: string;
  owner_id: string;
  state: AssetState;
  purge_reason: string | null;
  purged_at: string | null;
}

export interface AssetAdminResponse {
  asset: Asset;
}

export async function getStaffAsset(assetId: string): Promise<Asset> {
  const { data } = await getApiClient().get<AssetAdminResponse>(
    `/staff/assets/${assetId}`,
  );
  return data.asset;
}

export async function purgeAsset(
  assetId: string,
  reason: string,
): Promise<Asset> {
  const { data } = await getApiClient().post<AssetAdminResponse>(
    `/staff/assets/${assetId}/purge`,
    { reason },
  );
  return data.asset;
}
